import { Descope } from '@descope/react-sdk/flows';
import React, { useState } from 'react';
import {
  getAuthMfaChallengeRequestFailureReasonCode,
  publishAuthMfaChallengeRequestCompletedEvent,
  publishAuthMfaChallengeRequestedEvent,
} from '@shared/utils/analytics/events';
import { getOrCreateClientSessionId } from '@shared/utils/clientSessionId';
import { withClientSessionId } from '@shared/utils/descopeActionPayload';
import ResetMfaMethodSelectionScreen from './ResetMfaMethodSelectionScreen';
import ResetTotpVerificationScreen from './ResetTotpVerificationScreen';
import {
  RESET_SMS_MFA_INTERACTION_IDS,
  ResetSmsMfaScreenNames,
} from './screenMapping';
import OTPVerificationScreen from '../universal-login/screens/OTPVerificationScreen';

export const RESET_SMS_MFA_FLOW_ID = 'reset-sms-mfa';
export const RESET_TOTP_MFA_FLOW_ID = 'reset-totp-mfa';

const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;
type RawDescopeNext = (
  interactionId: string,
  payload: Record<string, unknown>
) => Promise<unknown>;

type FlowForm = Record<string, unknown>;
type DescopeContext = {
  error?: unknown;
  form?: FlowForm;
  [key: string]: unknown;
};
type FlowState = DescopeContext & {
  next?: DescopeNext;
  screenName?: string;
};

const getDescopeRedirectUrl = (): string => {
  const url = new URL(window.location.href);
  url.hash = '';
  return url.toString();
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const getErrorText = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  return typeof error.text === 'string' ? error.text : undefined;
};

const getErrorDetail = (error: unknown): unknown =>
  isRecord(error) && 'detail' in error ? error.detail : error;

const resetErrorState = (
  setState: React.Dispatch<React.SetStateAction<FlowState>>
) =>
  setState(prevState => ({
    ...prevState,
    error: {},
  }));

interface ResetMfaFlowProps {
  flowId: string;
}

const ResetMfaFlow: React.FC<ResetMfaFlowProps> = ({ flowId }) => {
  const [state, setState] = useState<FlowState>({ error: {} });
  const [form, setForm] = useState<FlowForm>({});
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [clientSessionId] = useState(getOrCreateClientSessionId);
  const flowForm = {
    ...(state?.form ?? {}),
    ...form,
  };

  return (
    <Descope
      flowId={flowId}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId(flowForm)}
      onScreenUpdate={(
        screenName: string,
        context: DescopeContext,
        next: RawDescopeNext
      ) => {
        const nextWithClientSessionId: DescopeNext = (interactionId, payload) =>
          next(interactionId, withClientSessionId(payload ?? {}));

        setState(prevState => ({
          ...prevState,
          ...context,
          next: nextWithClientSessionId,
          screenName,
          error: context?.error || {},
        }));

        const customScreenNames = [
          ResetSmsMfaScreenNames.SELECT_MFA,
          ResetSmsMfaScreenNames.VERIFY_SMS_OTP,
          ResetSmsMfaScreenNames.VERIFY_TOTP,
        ];

        return customScreenNames.includes(screenName as ResetSmsMfaScreenNames);
      }}
      onSuccess={() => {
        window.location.href = `${LINKTREE_BASE_URL}/admin/account`;
      }}
      onError={(error: unknown) => {
        setState(prevState => ({
          ...prevState,
          error: getErrorDetail(error),
        }));
      }}
    >
      {state?.screenName === ResetSmsMfaScreenNames.SELECT_MFA && (
        <ResetMfaMethodSelectionScreen
          mfaFlow="reset"
          onClickSms={async () => {
            if (state.next) {
              publishAuthMfaChallengeRequestedEvent(
                'login',
                'initial_route',
                'sms',
                'reset'
              );

              try {
                await state.next(
                  RESET_SMS_MFA_INTERACTION_IDS.SELECT_SMS,
                  flowForm
                );
                publishAuthMfaChallengeRequestCompletedEvent(
                  'login',
                  'initial_route',
                  'succeeded',
                  'sms',
                  'reset'
                );
              } catch (error) {
                publishAuthMfaChallengeRequestCompletedEvent(
                  'login',
                  'initial_route',
                  'failed',
                  'sms',
                  'reset',
                  getAuthMfaChallengeRequestFailureReasonCode(error)
                );
                throw error;
              }
            }
          }}
          onClickTotp={async () => {
            if (state.next) {
              await state.next(
                RESET_SMS_MFA_INTERACTION_IDS.SELECT_TOTP,
                flowForm
              );
            }
          }}
          errorText={getErrorText(state.error)}
        />
      )}
      {state?.screenName === ResetSmsMfaScreenNames.VERIFY_SMS_OTP && (
        <OTPVerificationScreen
          authFlow="login"
          otpChannel="sms"
          mfaFlow="reset"
          onFormUpdate={data => setForm(prevForm => ({ ...prevForm, ...data }))}
          onOTPVerificationClick={async (code: string) => {
            if (state.next) {
              await state.next(RESET_SMS_MFA_INTERACTION_IDS.SUBMIT_CODE, {
                ...flowForm,
                code,
              });
            }
          }}
          onClickResend={async () => {
            if (state.next) {
              await state.next(RESET_SMS_MFA_INTERACTION_IDS.RESEND, flowForm);
            }
          }}
          errorText={getErrorText(state.error)}
          context={{ ...state, form: flowForm }}
          resetError={() => resetErrorState(setState)}
        />
      )}
      {state?.screenName === ResetSmsMfaScreenNames.VERIFY_TOTP && (
        <ResetTotpVerificationScreen
          authFlow="login"
          mfaFlow="reset"
          onFormUpdate={data => setForm(prevForm => ({ ...prevForm, ...data }))}
          onCodeSubmit={async (code: string) => {
            if (state.next) {
              await state.next(RESET_SMS_MFA_INTERACTION_IDS.SUBMIT_CODE, {
                ...flowForm,
                code,
              });
            }
          }}
          errorText={getErrorText(state.error)}
          resetError={() => resetErrorState(setState)}
        />
      )}
    </Descope>
  );
};

const ResetSmsMfaFlow: React.FC = () => (
  <ResetMfaFlow flowId={RESET_SMS_MFA_FLOW_ID} />
);

export const ResetTotpMfaFlow: React.FC = () => (
  <ResetMfaFlow flowId={RESET_TOTP_MFA_FLOW_ID} />
);

export default ResetSmsMfaFlow;
